import { useAuth, type OrOfAnds, type PermissionString } from '@eis/core';

export interface UsePermissionsResult {
  can: (permission: PermissionString) => boolean;
  canAny: (permissions: readonly PermissionString[]) => boolean;
  canAll: (permissions: readonly PermissionString[]) => boolean;
  canGroups: (groups: OrOfAnds) => boolean;
}

/**
 * The sanctioned hook for permission-dependent *logic* — column definitions, disabled states,
 * whether to render a row action (Addendum A.2, enforcement layer 2).
 *
 * Reaching into the raw matcher or auth internals from module code is lint-banned. This hook and
 * `<RequirePermission>` are the only doors, which is precisely the AI-consistency fix: agents
 * compose one pattern instead of reinventing forty slightly different inline checks.
 */
export function usePermissions(): UsePermissionsResult {
  const { can, canAny, canAll, canGroups } = useAuth();
  return { can, canAny, canAll, canGroups };
}

export interface EntityActions {
  readonly canView: boolean;
  readonly canCreate: boolean;
  readonly canUpdate: boolean;
  readonly canDelete: boolean;
}

/**
 * The recurring list-action case that dominates the legacy code (`hasAccessUpdate/Delete` in ~40
 * components). Declaring it once here is what stops it being re-derived per screen.
 */
export function useEntityActions(entity: {
  readonly view?: PermissionString;
  readonly create?: PermissionString;
  readonly update?: PermissionString;
  readonly delete?: PermissionString;
}): EntityActions {
  const { can } = usePermissions();
  return {
    canView: entity.view !== undefined && can(entity.view),
    canCreate: entity.create !== undefined && can(entity.create),
    canUpdate: entity.update !== undefined && can(entity.update),
    canDelete: entity.delete !== undefined && can(entity.delete),
  };
}
